// Global Instructions Rule Applied!
//
// Deal form Execution tab — cap-table read-through card (Phase 4c, replaces
// the Phase-4 placeholder). Co-investors of a round = the NAMED investors of
// its cap-table version (view__deal_execution_context 'cap_table' key —
// current version tied to the deal's round, else latest non-draft). The full
// cap table lives on the company form's Cap Table tab; this card links there.
// Capture-in-place (owner-approved Option A, 2026-08-12): the header's
// Capture button opens the existing editor modal with this deal's round and
// close date preselected; the editor's own Save draft / Save & set current
// footer applies, and the deal context refetches on save.
import dayjs from 'dayjs'
import React, { useState } from 'react'
import { Button, Table, Tag } from 'antd'
import { showErrorToast } from '../../../core/utils/errorToast.tsx'
import { PlusOutlined } from '@ant-design/icons'
import { FormSection } from '../../../core/components/FormSection.tsx'
import { getAccessToken } from '../../../core/auth/authService'
import { store } from '../../../state_manager/store.ts'
import { useTabManagerContext } from '../../../core/app-layout/lib/TabManagerContext.tsx'
import {
  DealExecutionCapTable,
  DealExecutionCommitmentRollupRow,
  DealExecutionParticipation
} from '../../../state_manager/data_models/deal_execution_board_model.ts'
import { effectiveOurAmount } from './RoundInvestorsCard.tsx'
import { CapTableData } from '../../../state_manager/data_models/cap_table_model.ts'
import { CapTableSavePayload, fetchCapTable } from '../services/dealExecutionService.ts'
import CapTableEditorModal from './CapTableEditorModal.tsx'
import { pct, shares } from './CapTableTab.tsx'

interface CapTableSummaryCardProps {
  capTable: DealExecutionCapTable | null | undefined
  hasRound: boolean
  companyId: string | null | undefined
  companyName: string | null | undefined
  roundId?: string | null
  roundCloseDate?: string | null
  participations?: DealExecutionParticipation[]
  rollup?: DealExecutionCommitmentRollupRow[]
  /** Manager accounts of our managed_by_us funds — their bare lead-marker
   *  participation rows are ours, not co-investors */
  usManagerAccountIds?: string[]
  currencySymbol?: string | null
  onCaptured?: () => void | Promise<void>
}

export default function CapTableSummaryCard({
  capTable,
  hasRound,
  companyId,
  companyName,
  roundId,
  roundCloseDate,
  participations = [],
  rollup = [],
  usManagerAccountIds = [],
  currencySymbol,
  onCaptured
}: CapTableSummaryCardProps): React.ReactElement {
  const sym = currencySymbol || '$'
  // Co-investor views exclude our lead-marker rows (manager account, no
  // amount) — ours surface through the derived fund rows instead.
  const usManagerIds = new Set(usManagerAccountIds)
  const externalParticipations = participations.filter(
    (p) => !(p.accounts__id && usManagerIds.has(p.accounts__id))
  )
  const money = (value: number | null | undefined): string =>
    value == null
      ? '—'
      : `${sym}${Number(value).toLocaleString('en-US', { maximumFractionDigits: 2 })}`
  // Our derived per-fund/class rows rendered in the co-investor table with
  // the same columns (owner 2026-08-14). Kind 'ours' marks them: fund link,
  // derived units as issued/FD, and the amount sourced from the deal
  // commitment until the subscription books, then from the booked
  // asset-transaction amounts. The amount is per FUND, so it shows on the
  // fund's first class row only (a multi-class fund must not double-display).
  const seenAmountFunds = new Set<string>()
  const coInvestorRows = capTable
    ? [
        ...capTable.our_rows.map((r) => {
          const fundKey = r.funds__id || 'fund'
          const firstRowForFund = !seenAmountFunds.has(fundKey)
          seenAmountFunds.add(fundKey)
          const rollupRow = rollup.find((x) => x.funds__id === r.funds__id)
          const eff = firstRowForFund && rollupRow ? effectiveOurAmount(rollupRow) : null
          return {
            cap_table_holdings__id: `ours-${r.funds__id || 'fund'}-${r.cap_table_lines__class_name || ''}`,
            cap_table_holdings__holder_kind: 'ours',
            accounts__id: null as string | null,
            accounts__name: null as string | null,
            cap_table_holdings__holder_name: r.funds__name || 'Our funds',
            cap_table_lines__class_name: r.cap_table_lines__class_name,
            cap_table_lines__line_kind: r.cap_table_lines__line_kind,
            cap_table_lines__issued: r.cap_table_lines__issued,
            cap_table_lines__fully_diluted: r.cap_table_lines__fully_diluted,
            cap_table_holdings__issued: r.prog__units,
            cap_table_holdings__fully_diluted: r.prog__units,
            funds__id: r.funds__id ?? null,
            ours_amount: eff?.amount ?? null,
            ours_amount_source: eff?.source ?? null
          }
        }),
        ...capTable.co_investors.map((h) => ({
          ...h,
          funds__id: null as string | null,
          ours_amount: null as number | null,
          ours_amount_source: null as 'committed' | 'booked' | null
        }))
      ]
    : []

  // Invested amount for a holder row — matched from the round participations
  // by linked account, else by name (participations carry the early amounts;
  // holdings carry the priced share counts).
  const amountFor = (accountId: string | null | undefined, name: string | null | undefined) => {
    const match = participations.find(
      (p) =>
        (accountId && p.accounts__id === accountId) ||
        (!accountId &&
          name &&
          (p.round_participations__name || p.accounts__name || '').trim().toLowerCase() ===
            name.trim().toLowerCase())
    )
    return match?.round_participations__amount ?? null
  }
  const { openFormInTab } = useTabManagerContext()
  // Editor option lists (company rounds/securities) load lazily on first
  // Capture click — the card itself renders from the deal context alone.
  const [editorData, setEditorData] = useState<CapTableData | null>(null)
  const [editorInitial, setEditorInitial] = useState<CapTableSavePayload | null>(null)
  const [editorOpen, setEditorOpen] = useState(false)
  const [editorLoading, setEditorLoading] = useState(false)

  const openCompanyCapTable = (): void => {
    if (companyId) openFormInTab('companies', companyId, companyName || 'Company')
  }

  const openCapture = async (): Promise<void> => {
    if (!companyId || editorLoading) return
    setEditorLoading(true)
    const token = getAccessToken(store.getState().auth)
    const result = await fetchCapTable(token, companyId, null)
    setEditorLoading(false)
    if (!result.data) {
      showErrorToast(result.error || 'Failed to load cap-table editor data')
      return
    }
    setEditorData(result.data)
    setEditorInitial({
      account_id: companyId,
      kind: 'round',
      round_id: roundId ?? null,
      as_of_date: roundCloseDate || dayjs().format('YYYY-MM-DD'),
      notes: null,
      lines: []
    })
    setEditorOpen(true)
  }

  // Edit the displayed round snapshot IN PLACE (same semantics as the
  // company tab's Edit) — fetch the full line/holder detail first, the deal
  // context only carries the summary.
  const openEditDisplayed = async (): Promise<void> => {
    if (!companyId || !capTable || editorLoading) return
    setEditorLoading(true)
    const token = getAccessToken(store.getState().auth)
    const result = await fetchCapTable(token, companyId, capTable.cap_table_versions__id)
    setEditorLoading(false)
    const data = result.data
    if (!data || !data.version) {
      showErrorToast(result.error || 'Failed to load cap-table editor data')
      return
    }
    setEditorData(data)
    setEditorInitial({
      id: data.version.cap_table_versions__id,
      account_id: companyId,
      round_id: data.version.rounds__id ?? null,
      kind: data.version.cap_table_versions__kind,
      as_of_date: data.version.cap_table_versions__as_of_date,
      notes: data.version.cap_table_versions__notes ?? null,
      lines: data.lines.map((l) => ({
        id: l.cap_table_lines__id,
        security_ids: l.securities.map((s) => s.securities__id),
        class_name: l.cap_table_lines__class_name,
        issued: l.cap_table_lines__issued ?? null,
        fully_diluted: l.cap_table_lines__fully_diluted ?? null,
        issue_price: l.cap_table_lines__issue_price ?? null,
        is_esop_pool: l.cap_table_lines__is_esop_pool ?? false,
        line_kind: (l.cap_table_lines__line_kind || 'equity') as 'equity' | 'warrant' | 'convertible' | 'debt',
        sort_order: l.cap_table_lines__sort_order ?? 0,
        holders: l.holders.map((h) => ({
          id: h.cap_table_holdings__id,
          holder_kind: h.cap_table_holdings__holder_kind as 'account' | 'text' | 'other',
          holder_account_id: h.accounts__id ?? null,
          holder_name: h.cap_table_holdings__holder_name ?? null,
          issued: h.cap_table_holdings__issued ?? null,
          fully_diluted: h.cap_table_holdings__fully_diluted ?? null,
          sort_order: h.cap_table_holdings__sort_order ?? 0
        }))
      }))
    })
    setEditorOpen(true)
  }

  return (
    <FormSection
      title='Cap Table & Co-investors'
      titleColor='orange'
      scrollable={false}
      headerActions={
        companyId ? (
          <span className='flex items-center gap-2'>
            <button
              type='button'
              className='cursor-pointer text-blue-600 hover:text-blue-700 hover:underline bg-transparent border-0 p-0 text-xs'
              onClick={openCompanyCapTable}
            >
              Open cap table
            </button>
            {capTable && (
              <Button size='small' loading={editorLoading} onClick={openEditDisplayed}>
                Edit
              </Button>
            )}
            <Button
              size='small'
              type='primary'
              icon={<PlusOutlined />}
              loading={editorLoading}
              onClick={openCapture}
            >
              Capture version
            </Button>
          </span>
        ) : undefined
      }
    >
      {!capTable ? (
        externalParticipations.length > 0 ? (
          <div className='space-y-2'>
            <Table
              size='small'
              rowKey='round_participations__id'
              pagination={false}
              dataSource={externalParticipations}
              columns={[
                {
                  title: 'Co-investor',
                  key: 'investor',
                  render: (_, p) =>
                    p.round_participations__participant_kind === 'account' && p.accounts__id ? (
                      <span
                        className='cursor-pointer text-blue-600 hover:underline'
                        onClick={() =>
                          openFormInTab(
                            'companies',
                            p.accounts__id as string,
                            p.accounts__name || 'Account'
                          )
                        }
                      >
                        {p.accounts__name || 'Account'}
                        {p.round_participations__is_lead ? (
                          <Tag color='blue' className='ml-2'>
                            Lead
                          </Tag>
                        ) : null}
                      </span>
                    ) : (
                      <span>
                        {p.round_participations__name || '—'}
                        {p.round_participations__is_lead ? (
                          <Tag color='blue' className='ml-2'>
                            Lead
                          </Tag>
                        ) : null}
                      </span>
                    )
                },
                {
                  title: 'Amount',
                  key: 'amount',
                  align: 'right' as const,
                  render: (_, p) => (
                    <span className='tabular-nums'>{money(p.round_participations__amount)}</span>
                  )
                }
              ]}
            />
            <div className='text-xs text-gray-400'>
              Amounts from the Round Investors card — shareholding appears once a cap-table
              version is captured for this round.
            </div>
          </div>
        ) : (
          <div className='text-sm text-gray-400 py-2'>
            {hasRound
              ? "No current cap-table version is tied to this deal's round yet — capture one with the button above."
              : 'Link a funding round to read its cap table and co-investors here.'}
          </div>
        )
      ) : (
        <div className='space-y-2'>
          <div className='flex items-center gap-2 text-xs text-gray-500 flex-wrap'>
            <Tag color={capTable.cap_table_versions__status === 'current' ? 'green' : 'default'}>
              {capTable.cap_table_versions__status}
            </Tag>
            <span>round snapshot as of {dayjs(capTable.cap_table_versions__as_of_date).format('MM/DD/YYYY')}</span>
            {capTable.prog__restatement && (
              <span className='text-amber-600'>
                · restated
                {capTable.prog__restatement.cap_table_versions__adjustment_factor != null
                  ? ` ×${capTable.prog__restatement.cap_table_versions__adjustment_factor}`
                  : ''}
                {capTable.prog__restatement.cap_table_versions__as_of_date
                  ? ` on ${dayjs(capTable.prog__restatement.cap_table_versions__as_of_date).format('MM/DD/YYYY')}`
                  : ''}{' '}
                — the company cap table holds the current counts
              </span>
            )}
            {(capTable.issued_total ?? 0) > 0 || (capTable.fd_total ?? 0) > 0 ? (
              <>
                <span>· Outstanding {shares(capTable.issued_total)}</span>
                <span>· FD {shares(capTable.fd_total)}</span>
              </>
            ) : (
              <span>· no stock issued (convertibles / debt only)</span>
            )}
          </div>
          {capTable.lines.length > 0 && (
            <Table
              size='small'
              rowKey='cap_table_lines__class_name'
              pagination={false}
              dataSource={capTable.lines}
              columns={[
                {
                  title: 'Class',
                  dataIndex: 'cap_table_lines__class_name',
                  render: (v, line) => (
                    <span>
                      {v}
                      {line.cap_table_lines__is_esop_pool ? <Tag className='ml-2'>ESOP</Tag> : null}
                      {line.cap_table_lines__line_kind === 'warrant' ? (
                        <Tag className='ml-2' color='geekblue'>Warrant</Tag>
                      ) : null}
                      {line.cap_table_lines__line_kind === 'convertible' ? (
                        <Tag className='ml-2' color='orange'>Convertible</Tag>
                      ) : null}
                      {line.cap_table_lines__line_kind === 'debt' ? (
                        <Tag className='ml-2' color='volcano'>Debt</Tag>
                      ) : null}
                    </span>
                  )
                },
                {
                  title: 'Issued',
                  dataIndex: 'cap_table_lines__issued',
                  align: 'right' as const,
                  render: (v) => shares(v as number)
                },
                {
                  title: '% Outstanding',
                  dataIndex: 'prog__issued_pct',
                  align: 'right' as const,
                  render: (v) => (v != null ? pct(v as number) : '—')
                },
                {
                  title: 'FD',
                  dataIndex: 'cap_table_lines__fully_diluted',
                  align: 'right' as const,
                  render: (v) => shares(v as number)
                },
                {
                  title: '% FD',
                  dataIndex: 'prog__fd_pct',
                  align: 'right' as const,
                  render: (v) => (v != null ? pct(v as number) : '—')
                }
              ]}
            />
          )}
          <div className='text-xs font-semibold text-gray-500 uppercase tracking-wide'>
            Co-investors &amp; Our Position
          </div>
          {coInvestorRows.length === 0 ? (
            <div className='text-sm text-gray-400'>
              No named investors on this cap-table version.
            </div>
          ) : (
            <Table
              size='small'
              rowKey='cap_table_holdings__id'
              pagination={false}
              dataSource={coInvestorRows}
              columns={[
                {
                  title: 'Investor',
                  key: 'holder',
                  render: (_, h) => {
                    if (h.cap_table_holdings__holder_kind === 'ours') {
                      return (
                        <span>
                          <span
                            className='cursor-pointer text-blue-600 hover:underline'
                            onClick={() =>
                              h.funds__id &&
                              openFormInTab(
                                'funds',
                                h.funds__id,
                                h.cap_table_holdings__holder_name || 'Fund'
                              )
                            }
                          >
                            {h.cap_table_holdings__holder_name || 'Our funds'}
                          </span>
                          <Tag color='purple' className='ml-2'>
                            Ours
                          </Tag>
                        </span>
                      )
                    }
                    return h.cap_table_holdings__holder_kind === 'account' && h.accounts__id ? (
                      <span
                        className='cursor-pointer text-blue-600 hover:underline'
                        onClick={() =>
                          openFormInTab('companies', h.accounts__id as string, h.accounts__name || 'Account')
                        }
                      >
                        {h.accounts__name || 'Account'}
                      </span>
                    ) : (
                      h.cap_table_holdings__holder_name || h.accounts__name || '—'
                    )
                  }
                },
                {
                  title: 'Class',
                  dataIndex: 'cap_table_lines__class_name',
                  render: (v) => v || '—'
                },
                {
                  title: 'Amount',
                  key: 'amount',
                  align: 'right' as const,
                  render: (_, h) => (
                    <span className='tabular-nums'>
                      {h.cap_table_holdings__holder_kind === 'ours' ? (
                        <>
                          {money(h.ours_amount)}
                          {h.ours_amount_source === 'booked' && (
                            <Tag color='green' className='ml-1'>
                              booked
                            </Tag>
                          )}
                        </>
                      ) : (
                        money(
                          amountFor(
                            h.accounts__id,
                            h.cap_table_holdings__holder_name || h.accounts__name
                          )
                        )
                      )}
                    </span>
                  )
                },
                {
                  title: 'Issued',
                  dataIndex: 'cap_table_holdings__issued',
                  align: 'right' as const,
                  render: (v) => shares(v as number)
                },
                {
                  title: '% Outstanding',
                  key: 'pct_issued',
                  align: 'right' as const,
                  // Only equity lines are outstanding stock — a warrant /
                  // convertible / debt holding never shows an outstanding %.
                  render: (_, h) =>
                    (h.cap_table_lines__line_kind ?? 'equity') === 'equity' &&
                    capTable.issued_total &&
                    h.cap_table_holdings__issued != null
                      ? pct((Number(h.cap_table_holdings__issued) / Number(capTable.issued_total)) * 100)
                      : '—'
                },
                {
                  title: 'FD',
                  dataIndex: 'cap_table_holdings__fully_diluted',
                  align: 'right' as const,
                  render: (v) => shares(v as number)
                },
                {
                  title: '% FD',
                  key: 'pct_fd',
                  align: 'right' as const,
                  // Equity and warrant lines feed the FD total; convertible /
                  // debt holdings show no FD %.
                  render: (_, h) =>
                    ['equity', 'warrant'].includes(h.cap_table_lines__line_kind ?? 'equity') &&
                    capTable.fd_total &&
                    h.cap_table_holdings__fully_diluted != null
                      ? pct((Number(h.cap_table_holdings__fully_diluted) / Number(capTable.fd_total)) * 100)
                      : '—'
                },
                {
                  title: '% Class',
                  key: 'pct_class',
                  align: 'right' as const,
                  render: (_, h) =>
                    h.cap_table_lines__issued && h.cap_table_holdings__issued != null
                      ? pct((Number(h.cap_table_holdings__issued) / Number(h.cap_table_lines__issued)) * 100)
                      : '—'
                }
              ]}
            />
          )}
        </div>
      )}

      {companyId && editorData && (
        <CapTableEditorModal
          open={editorOpen}
          accountId={companyId}
          initial={editorInitial}
          companyRounds={editorData.company_rounds}
          companySecurities={editorData.company_securities}
          roundParticipations={editorData.round_participations.filter(
            (p) => !(p.accounts__id && usManagerIds.has(p.accounts__id))
          )}
          onClose={() => setEditorOpen(false)}
          onSaved={async () => {
            setEditorOpen(false)
            if (onCaptured) await onCaptured()
          }}
        />
      )}
    </FormSection>
  )
}
